import type { LearningExercise } from "../types";

// Claim Lab (EPÜ-Anspruchsanalyse), Ausbau von 4 -> 12 Fragen. Von Vincent freigegeben
// (2026-06-03, question-reviewer-Gate 8 OK / 0 FIX / 0 AMBIG) -> reviewStatus "beta-ready".
// pathId "epu-eqe" + skillTags ["claim-analysis"] => filterForMode ordnet sie dem Claim-Lab-Modus zu.
// Format wie src/data/flashcards.ts (options: string[], answers: [Index]); in questions.ts gespreadet.
export const claimLab: LearningExercise[] = [
  {
    id: "cl-claim-001",
    pathId: "epu-eqe",
    mode: "claim",
    kind: "single",
    topic: "Schutzbereich - Art. 69 EPÜ",
    title: "Auslegung des Schutzbereichs",
    prompt: "Wie wird der Schutzbereich eines europäischen Patents nach Art. 69 EPÜ und dem Auslegungsprotokoll bestimmt?",
    options: [
      "Allein durch den strengen Wortlaut der Patentansprüche, ohne Heranziehung von Beschreibung und Zeichnungen.",
      "Durch die Patentansprüche, wobei Beschreibung und Zeichnungen zur Auslegung heranzuziehen sind — Mittelweg zwischen striktem Wortlaut und bloßer Richtlinie.",
      "Allein durch die Beschreibung; die Ansprüche dienen nur als grobe Richtlinie.",
      "Durch den Willen des Anmelders, wie er sich aus der Erteilungsakte ergibt.",
    ],
    answers: [1],
    explanation:
      "Art. 69 EPÜ: Der Schutzbereich wird durch die Patentansprüche bestimmt; Beschreibung und Zeichnungen sind zur Auslegung heranzuziehen. Das Auslegungsprotokoll (Art. 1) verlangt einen Mittelweg, der angemessenen Schutz für den Patentinhaber mit hinreichender Rechtssicherheit für Dritte verbindet — weder bloßer Wortlaut noch bloße Richtlinie.",
    sourceIds: ["epcArt69"],
    sources: [{ id: "epcArt69", label: "Art. 69 EPÜ", url: "https://www.epo.org/de/legal/epc/2020/art69.html" }],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Schutzbereich",
    skillTags: ["claim-analysis"],
    reviewStatus: "beta-ready",
  },
  {
    id: "cl-claim-002",
    pathId: "epu-eqe",
    mode: "claim",
    kind: "single",
    topic: "Klarheit - Art. 84 EPÜ",
    title: "Anforderungen an die Patentansprüche (Art. 84)",
    prompt: "Welche Anforderung stellt Art. 84 EPÜ an die Patentansprüche?",
    options: [
      "Sie müssen deutlich und knapp gefasst sein und von der Beschreibung gestützt werden.",
      "Sie müssen mindestens ein Ausführungsbeispiel wörtlich wiederholen.",
      "Sie müssen den nächstliegenden Stand der Technik ausdrücklich benennen.",
      "Sie müssen den wirtschaftlichen Wert der Erfindung angeben.",
    ],
    answers: [0],
    explanation:
      "Art. 84 EPÜ: Die Patentansprüche müssen den Gegenstand angeben, für den Schutz begehrt wird; sie müssen deutlich und knapp gefasst sein und von der Beschreibung gestützt werden (Klarheit + Stützung). Ein Ausführungsbeispiel, die Nennung des Stands der Technik oder wirtschaftliche Angaben verlangt Art. 84 nicht.",
    sourceIds: ["epcArt84"],
    sources: [{ id: "epcArt84", label: "Art. 84 EPÜ", url: "https://www.epo.org/de/legal/epc/2020/art84.html" }],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Klarheit",
    skillTags: ["claim-analysis"],
    reviewStatus: "beta-ready",
  },
  {
    id: "cl-claim-003",
    pathId: "epu-eqe",
    mode: "claim",
    kind: "single",
    topic: "Anspruchsform - Regel 43(1) EPÜ",
    title: "Zweiteilige Anspruchsform",
    prompt: "Was kennzeichnet die zweiteilige Anspruchsform nach Regel 43(1) EPÜ?",
    options: [
      "Oberbegriff mit den aus dem Stand der Technik bekannten Merkmalen, kennzeichnender Teil mit den neuen/erfinderischen Merkmalen (soweit zweckmäßig).",
      "Ein Hauptanspruch und genau ein abhängiger Anspruch.",
      "Trennung in einen technischen und einen wirtschaftlichen Teil.",
      "Aufteilung in Erzeugnis- und Verfahrensanspruch innerhalb desselben Anspruchs.",
    ],
    answers: [0],
    explanation:
      "Regel 43(1) EPÜ: Wo zweckmäßig, ist der Anspruch zweiteilig zu fassen — Oberbegriff mit den gemeinsam mit dem nächstliegenden Stand der Technik bekannten Merkmalen, gefolgt vom kennzeichnenden Teil (gekennzeichnet durch ...) mit den Merkmalen, für die in Verbindung Schutz begehrt wird.",
    sourceIds: ["epcR43"],
    sources: [{ id: "epcR43", label: "Regel 43 EPÜ", url: "https://www.epo.org/de/legal/epc/2020/r43.html" }],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Anspruchsform",
    skillTags: ["claim-analysis"],
    reviewStatus: "beta-ready",
  },
  {
    id: "cl-claim-004",
    pathId: "epu-eqe",
    mode: "claim",
    kind: "single",
    topic: "Abhängige Ansprüche - Regel 43(4) EPÜ",
    title: "Bedeutung eines abhängigen Anspruchs",
    prompt: "Was gilt für einen abhängigen Patentanspruch?",
    options: [
      "Er enthält alle Merkmale des Anspruchs, auf den er rückbezogen ist, und zusätzliche Merkmale.",
      "Er ist unabhängig vom Hauptanspruch gültig, auch wenn dieser wegfällt.",
      "Er darf keine eigenen technischen Merkmale enthalten.",
      "Er bestimmt den Schutzbereich weiter als der Hauptanspruch.",
    ],
    answers: [0],
    explanation:
      "Ein abhängiger Anspruch (Regel 43(3),(4) EPÜ) umfasst sämtliche Merkmale des in Bezug genommenen Anspruchs und fügt weitere Merkmale hinzu. Er ist damit enger (kleinerer Schutzbereich) und fällt grundsätzlich mit dem Hauptanspruch, kann aber als Rückzugsposition (fall-back) dienen.",
    sourceIds: ["epcR43"],
    sources: [{ id: "epcR43", label: "Regel 43 EPÜ", url: "https://www.epo.org/de/legal/epc/2020/r43.html" }],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Anspruchsstruktur",
    skillTags: ["claim-analysis"],
    reviewStatus: "beta-ready",
  },
  {
    id: "cl-claim-005",
    pathId: "epu-eqe",
    mode: "claim",
    kind: "single",
    topic: "Anspruchskategorien - Art. 64(2) EPÜ",
    title: "Schutz des unmittelbaren Verfahrenserzeugnisses",
    prompt: "Ein Patent schützt ein Herstellungsverfahren. Wie weit reicht der Schutz nach Art. 64(2) EPÜ?",
    options: [
      "Der Schutz erstreckt sich auch auf die durch das Verfahren unmittelbar hergestellten Erzeugnisse.",
      "Der Schutz beschränkt sich strikt auf die Durchführung des Verfahrens; Erzeugnisse sind nie erfasst.",
      "Der Schutz erfasst alle gleichartigen Erzeugnisse, unabhängig vom Herstellungsverfahren.",
      "Der Schutz erstreckt sich nur auf Erzeugnisse, die im Anspruch ausdrücklich genannt sind.",
    ],
    answers: [0],
    explanation:
      "Art. 64(2) EPÜ: Ist Gegenstand des Patents ein Verfahren, so erstreckt sich der Schutz auch auf die durch das Verfahren unmittelbar hergestellten Erzeugnisse. Nicht erfasst sind gleichartige, aber anders hergestellte Erzeugnisse (kein absoluter Stoffschutz über das Verfahren).",
    sourceIds: ["epcArt64"],
    sources: [{ id: "epcArt64", label: "Art. 64 EPÜ", url: "https://www.epo.org/de/legal/epc/2020/art64.html" }],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Kategorien",
    skillTags: ["claim-analysis"],
    reviewStatus: "beta-ready",
  },
  {
    id: "cl-claim-006",
    pathId: "epu-eqe",
    mode: "claim",
    kind: "single",
    topic: "Anspruchsbreite - offene/geschlossene Formulierung",
    title: "comprising vs. consisting of",
    prompt: "Worin liegt der Unterschied zwischen einem Anspruch mit 'umfassend' (comprising) und 'bestehend aus' (consisting of)?",
    options: [
      "'umfassend' ist offen (weitere, nicht genannte Merkmale zulässig); 'bestehend aus' ist abgeschlossen (nur die genannten Merkmale).",
      "Beide sind bedeutungsgleich und ohne Einfluss auf den Schutzbereich.",
      "'bestehend aus' ist offen, 'umfassend' ist abgeschlossen.",
      "Beide Formulierungen sind nach Art. 84 EPÜ unzulässig.",
    ],
    answers: [0],
    explanation:
      "Bei der Anspruchsauslegung ist 'umfassend/enthaltend' (comprising) offen — der Gegenstand darf zusätzliche, nicht genannte Merkmale aufweisen. 'bestehend aus' (consisting of) ist abgeschlossen — nur die genannten Merkmale, keine weiteren wesentlichen Bestandteile. Die Wahl beeinflusst Schutzbreite und Abgrenzung zum Stand der Technik.",
    sourceIds: ["epcArt69"],
    sources: [{ id: "epcArt69", label: "Art. 69 EPÜ", url: "https://www.epo.org/de/legal/epc/2020/art69.html" }],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Anspruchsbreite",
    skillTags: ["claim-analysis"],
    reviewStatus: "beta-ready",
  },
  {
    id: "cl-claim-007",
    pathId: "epu-eqe",
    mode: "claim",
    kind: "single",
    topic: "Änderungen - Art. 123(2) EPÜ",
    title: "Unzulässige Zwischenverallgemeinerung",
    prompt: "Ein Anspruch wird im Prüfungsverfahren um ein einzelnes Merkmal ergänzt, das ursprünglich nur in Kombination mit weiteren Merkmalen offenbart war. Was ist das Problem?",
    options: [
      "Unzulässige Zwischenverallgemeinerung (intermediate generalisation) — Verstoß gegen Art. 123(2) EPÜ.",
      "Kein Problem, solange der Anspruch dadurch enger wird.",
      "Verstoß gegen Art. 123(3) EPÜ, weil der Schutzbereich erweitert wird.",
      "Verstoß gegen Art. 84 EPÜ wegen mangelnder Knappheit.",
    ],
    answers: [0],
    explanation:
      "Art. 123(2) EPÜ verbietet eine Änderung, deren Gegenstand über den Inhalt der ursprünglichen Anmeldung hinausgeht (Goldstandard: unmittelbar und eindeutig offenbart, G 2/10). Ein isoliert herausgegriffenes Merkmal aus einer ursprünglich nur als Kombination offenbarten Merkmalsgruppe ist eine unzulässige Zwischenverallgemeinerung — auch wenn der Anspruch dadurch enger wird.",
    sourceIds: ["epcArt123"],
    sources: [{ id: "epcArt123", label: "Art. 123 EPÜ", url: "https://www.epo.org/de/legal/epc/2020/art123.html" }],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Änderungen",
    skillTags: ["claim-analysis"],
    reviewStatus: "beta-ready",
  },
  {
    id: "cl-claim-008",
    pathId: "epu-eqe",
    mode: "claim",
    kind: "single",
    topic: "Verletzung - Merkmalsanalyse",
    title: "Wortsinngemäße Verletzung",
    prompt: "Wann liegt eine wortsinngemäße (unmittelbare) Verletzung eines Patentanspruchs vor?",
    options: [
      "Wenn die angegriffene Ausführungsform sämtliche Merkmale des Anspruchs verwirklicht.",
      "Wenn die angegriffene Ausführungsform mindestens ein Merkmal des Anspruchs verwirklicht.",
      "Wenn die angegriffene Ausführungsform demselben technischen Zweck dient.",
      "Wenn die angegriffene Ausführungsform die Mehrzahl der Merkmale verwirklicht.",
    ],
    answers: [0],
    explanation:
      "Eine wortsinngemäße Verletzung setzt voraus, dass die angegriffene Ausführungsform ALLE Merkmale des Patentanspruchs verwirklicht (Merkmalsanalyse, alle-Merkmale-Regel). Fehlt auch nur ein Merkmal, scheidet eine wortsinngemäße Verletzung aus; in Betracht kommt dann allenfalls eine Verletzung mit äquivalenten Mitteln.",
    sourceIds: ["epcArt69"],
    sources: [{ id: "epcArt69", label: "Art. 69 EPÜ", url: "https://www.epo.org/de/legal/epc/2020/art69.html" }],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Verletzung",
    skillTags: ["claim-analysis"],
    reviewStatus: "beta-ready",
  },
];
